import {
  Anchor,
  AspectRatio,
  Box,
  Button,
  Group,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { Popover } from '~/components/MantineOverlays';
import { IconArrowRight, IconInfoCircle } from '@tabler/icons-react';
import { useMemo } from 'react';
import { CrucibleCard } from '~/components/Cards/CrucibleCard';
import { ImageCard } from '~/components/Cards/ImageCard';
import { RemixFlyoutLayoutProvider } from '~/components/RemixGallery/remix-flyout-layout';
import { ModelCard } from '~/components/Cards/ModelCard';
import { HomeBlockWrapper } from '~/components/HomeBlocks/HomeBlockWrapper';
import { useApplyHiddenPreferences } from '~/components/HiddenPreferences/useApplyHiddenPreferences';
import { HostCrucibleTile } from '~/components/HomeBlocks/HostCrucibleTile';
import { layoutCrucibleRow, useGridSlots } from '~/components/HomeBlocks/useGridSlots';
import { ITEMS_PER_ROW } from '~/components/HomeBlocks/homeBlockItems';
import { SelectionNote } from '~/components/HomeBlocks/SelectionNote';
import {
  dedupeOrder,
  useDedupedCappedItems,
  useModelClaimKeys,
} from '~/components/HomeBlocks/homeBlockDedupe';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useViewerEventDecorations } from '~/components/Decorations/useViewerEventDecorations';
import { ImagesProvider } from '~/components/Image/Providers/ImagesProvider';
import { useHydratedImageReactions } from '~/components/Reaction/useHydratedImageReactions';
import { CustomMarkdown } from '~/components/Markdown/CustomMarkdown';
import { NextLink as Link } from '~/components/NextLink/NextLink';
import type { HomeBlockMetaSchema } from '~/server/schema/home-block.schema';
import type { FeedBlockItems } from '~/server/services/home-block.service';
import { shuffle } from '~/utils/array-helpers';
import { trpc } from '~/utils/trpc';
import classes from '~/components/HomeBlocks/HomeBlock.module.scss';

type Props = { homeBlockId: number; metadata: HomeBlockMetaSchema; blockIndex: number };

export const FeedHomeBlock = (props: Props) => {
  const { data: homeBlock, isLoading } = trpc.homeBlock.getHomeBlock.useQuery(
    { id: props.homeBlockId },
    { trpc: { context: { skipBatch: true } } }
  );

  // An event's crucibles block is configured ahead of the event and has nothing to show until a
  // moderator tags one, so it hides rather than showing an empty shelf. A block with no tag set
  // resolves no items at all, and hides the same way.
  const isCrucibles = props.metadata.feed?.entity === 'crucibles';
  if (isCrucibles && !isLoading && !homeBlock?.feedItems?.items.length) return null;

  return (
    <HomeBlockWrapper py={32}>
      <FeedHomeBlockContent {...props} feedItems={homeBlock?.feedItems} isLoading={isLoading} />
    </HomeBlockWrapper>
  );
};

/**
 * Renders a slice of an existing feed under the block's saved filters. Presentation
 * mirrors CollectionHomeBlock (same grid, rows and per-user cap) — the difference is
 * only where the items come from.
 */
const FeedHomeBlockContent = ({
  metadata,
  blockIndex,
  feedItems,
  isLoading,
}: Props & { feedItems?: FeedBlockItems; isLoading: boolean }) => {
  const rows = metadata.feed?.rows ?? 2;
  const currentUser = useCurrentUser();
  const title = metadata.title ?? 'Feed';

  // Description lives in the info popover next to the title, matching Featured Models
  // and Featured Images. Signed-out visitors get it inline instead, since the header
  // is the only place that context can land for them.
  const header = (
    <Stack gap="sm">
      <Group gap="xs" justify="space-between" className={classes.header}>
        <Group wrap="nowrap">
          <Title className={classes.title} order={1} lineClamp={1}>
            {title}{' '}
          </Title>
          {currentUser && (metadata.description || metadata.selectionNote) && (
            <Popover withArrow width={380}>
              <Popover.Target>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label="About this section"
                  display="inline-block"
                  style={{ lineHeight: 0.3, cursor: 'pointer' }}
                  color="white"
                >
                  <IconInfoCircle size={20} />
                </Box>
              </Popover.Target>
              <Popover.Dropdown maw="100%">
                <Text fw={500} size="lg" mb="xs">
                  {title}
                </Text>
                {metadata.description && (
                  <Text component="div" size="sm" mb="xs">
                    <CustomMarkdown allowedElements={['a']} unwrapDisallowed>
                      {metadata.description}
                    </CustomMarkdown>
                  </Text>
                )}
                {metadata.link && (
                  <Link legacyBehavior href={metadata.link} passHref>
                    <Anchor size="sm">
                      <Group gap={4}>
                        <Text inherit>{metadata.linkText ?? 'View All'}</Text>
                        <IconArrowRight size={16} />
                      </Group>
                    </Anchor>
                  </Link>
                )}
                <SelectionNote note={metadata.selectionNote} />
              </Popover.Dropdown>
            </Popover>
          )}
        </Group>
        {metadata.link && (
          <Link legacyBehavior href={metadata.link} passHref>
            <Button
              className={classes.expandButton}
              component="a"
              variant="subtle"
              rightSection={<IconArrowRight size={16} />}
            >
              {metadata.linkText ?? 'View All'}
            </Button>
          </Link>
        )}
      </Group>
      {metadata.description && !currentUser && (
        <div className="text-base">
          <CustomMarkdown allowedElements={['a']} unwrapDisallowed>
            {metadata.description}
          </CustomMarkdown>
        </div>
      )}
    </Stack>
  );

  // Each entity gets its own grid component so hidden-preference filtering and the
  // card union stay concretely typed instead of being cast at the render site.
  const grid =
    isLoading || !feedItems ? (
      <FeedSkeleton rows={rows} />
    ) : feedItems.entity === 'crucibles' ? (
      <CrucibleFeedGrid items={feedItems.items} rows={rows} eventTag={metadata.feed?.eventTag} />
    ) : feedItems.entity === 'images' ? (
      <ImageFeedGrid
        items={feedItems.items}
        rows={rows}
        maxPerUser={metadata.feed?.maxPerUser}
        order={dedupeOrder(blockIndex)}
      />
    ) : (
      <ModelFeedGrid
        items={feedItems.items}
        rows={rows}
        maxPerUser={metadata.feed?.maxPerUser}
        order={dedupeOrder(blockIndex)}
      />
    );

  return (
    <div style={{ '--rows': rows } as React.CSSProperties}>
      <Box mb="md">{header}</Box>
      {grid}
      {metadata.footer && (
        <Stack mt="md">
          <div className="mb-2 text-sm">
            <CustomMarkdown allowedElements={['a']} unwrapDisallowed>
              {metadata.footer}
            </CustomMarkdown>
          </div>
        </Stack>
      )}
    </div>
  );
};

const FeedSkeleton = ({ rows }: { rows: number }) => (
  <div className={classes.grid}>
    {Array.from({ length: ITEMS_PER_ROW * rows }).map((_, index) => (
      <AspectRatio ratio={7 / 9} key={index} className="m-2">
        <Skeleton width="100%" />
      </AspectRatio>
    ))}
  </div>
);

/**
 * Rotate the fetched pool so the shelf isn't identical on every visit, matching how
 * Collection and Featured Models blocks behave.
 *
 * Copies before shuffling: `shuffle` sorts in place, and the array here belongs to the
 * React Query cache.
 */
function useShuffled<T>(items: T[]) {
  return useMemo(() => shuffle([...items]), [items]);
}

type GridProps<T> = { items: T; rows: number; maxPerUser?: number; order: number };

function ImageFeedGrid({
  items,
  rows,
  maxPerUser,
  order,
}: GridProps<Extract<FeedBlockItems, { entity: 'images' }>['items']>) {
  const rotated = useShuffled(items);
  const { loadingPreferences, items: filtered } = useApplyHiddenPreferences({
    type: 'images',
    data: rotated,
  });
  // The block's payload is shared between viewers, so it arrives with `reactions: []` for
  // everyone. Passed straight into the cap rather than held in a binding of its own, which
  // removes the INVITED mistake of rendering the un-hydrated list — not every one: `filtered` is
  // still in scope, and so is what `ImagesProvider` is handed below.
  //
  // BEFORE the cap because the cap depends on what earlier blocks claimed through a shared store,
  // so it changes as they resolve — hydrating after it re-keyed the query and stranded the answer.
  // It does NOT buy the detail dialog anything: the dialog browses the capped list, which is
  // hydrated wherever this sits.
  const visible = useDedupedCappedItems(
    useHydratedImageReactions(useViewerEventDecorations(filtered, { entity: 'image' }), {
      entity: 'image',
    }),
    {
      order,
      entity: 'image',
      rows,
      maxPerUser,
    }
  );

  if (loadingPreferences) return <FeedSkeleton rows={rows} />;

  return (
    <div className={classes.grid} style={{ '--count': visible.length } as React.CSSProperties}>
      <RemixFlyoutLayoutProvider layout="side">
        <ImagesProvider images={visible}>
          {visible.map((item) => (
            <div key={item.id} className="p-2">
              <ImageCard data={item} />
            </div>
          ))}
        </ImagesProvider>
      </RemixFlyoutLayoutProvider>
    </div>
  );
}

function ModelFeedGrid({
  items,
  rows,
  maxPerUser,
  order,
}: GridProps<Extract<FeedBlockItems, { entity: 'models' }>['items']>) {
  const rotated = useShuffled(items);
  const { loadingPreferences, items: filtered } = useApplyHiddenPreferences({
    type: 'models',
    data: rotated,
  });
  const keysFor = useModelClaimKeys();
  const visible = useDedupedCappedItems(useViewerEventDecorations(filtered, { entity: 'model' }), {
    order,
    entity: 'model',
    rows,
    maxPerUser,
    keysFor,
  });

  if (loadingPreferences) return <FeedSkeleton rows={rows} />;

  return (
    <div className={classes.grid} style={{ '--count': visible.length } as React.CSSProperties}>
      {visible.map((item) => (
        <div key={item.id} className="p-2">
          <ModelCard data={item} forceInView />
        </div>
      ))}
    </div>
  );
}

/** Already in the server's order (running first, then by prize pool), so it is not shuffled. */
function CrucibleFeedGrid({
  items,
  rows,
  eventTag,
}: Omit<
  GridProps<Extract<FeedBlockItems, { entity: 'crucibles' }>['items']>,
  'maxPerUser' | 'order'
> & { eventTag?: string }) {
  const currentUser = useCurrentUser();
  const { loadingPreferences, items: hiddenFiltered } = useApplyHiddenPreferences({
    type: 'crucibles',
    data: items,
  });
  const { gridRef, slots } = useGridSlots(rows, !!eventTag, loadingPreferences);

  if (loadingPreferences) return <FeedSkeleton rows={rows} />;

  const { cards: visible, showTile } = layoutCrucibleRow(hiddenFiltered, eventTag, slots);

  return (
    <div
      ref={gridRef}
      className={classes.grid}
      style={{ '--count': visible.length + (showTile ? 1 : 0) } as React.CSSProperties}
    >
      {visible.map((item) => (
        <div key={item.id} className="p-2">
          <CrucibleCard data={item} />
        </div>
      ))}
      {showTile && eventTag && (
        <div className="p-2">
          <HostCrucibleTile eventTag={eventTag} signedIn={!!currentUser} />
        </div>
      )}
    </div>
  );
}
